import { useMemo, useState } from "react";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import { FilterIcon, PlusIcon } from "@icons";
import {
    buildFlatItems,
    plainFlatItemOptions as plainOptions,
} from "./PickerPlayground.lib";
import { VariantGrid, VariantItem } from "./playgroundControls";

/**
 * Any `Button` as the trigger: Base UI renders the consumer element, the root
 * fills its label from the selection (`Picker.Button` is the childless trigger
 * element, same props as `Button`; any `Button` with its own children still
 * works as a trigger; icon-only buttons keep their glyph) and adds the
 * trailing chevron unless the element is icon-only, brings its own
 * `rightIcon` or `chevron="none"`. Omitting `trigger` renders the primary md
 * default; `clearable` works on every Button.
 */
export const PickerButtonTriggerGrid = () => {
    const items = useMemo(() => buildFlatItems(plainOptions), []);
    const [value, setValue] = useState<string | null>(items[1].value);

    return (
        <VariantGrid title="Button triggers" columns="wide">
            <VariantItem label="Default (trigger omitted): primary md, chevron, clearable">
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    clearable
                />
            </VariantItem>
            <VariantItem label='trigger={<Button size="xl" block />} · clearable'>
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    clearable
                    trigger={
                        <Picker.Button variant="primary" size="xl" block />
                    }
                />
            </VariantItem>
            <VariantItem label="Ghost icon-only (kebab-less picker): no chevron">
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    trigger={
                        <Button
                            variant="ghost"
                            iconOnly
                            aria-label="Choose account"
                        >
                            <FilterIcon />
                        </Button>
                    }
                />
            </VariantItem>
            <VariantItem label="Secondary with left icon, label from the selection">
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    placeholder="Add account"
                    trigger={
                        <Picker.Button
                            variant="secondary"
                            size="md"
                            leftIcon={<PlusIcon />}
                        />
                    }
                />
            </VariantItem>
            <VariantItem label="Secondary · searchable · clearable">
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    searchable
                    searchPlaceholder="Search accounts"
                    clearable
                    trigger={<Picker.Button variant="secondary" size="md" />}
                />
            </VariantItem>
            <VariantItem label="Link-style">
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    trigger={<Picker.Button variant="link" size="sm" />}
                />
            </VariantItem>
            <VariantItem label='Tertiary · chevron="none"'>
                <Picker.Select
                    items={items}
                    value={value}
                    onValueChange={setValue}
                    chevron="none"
                    trigger={<Picker.Button variant="tertiary" size="md" />}
                />
            </VariantItem>
        </VariantGrid>
    );
};
